import axios from "axios";
import { apiUrl } from "../config/environment";

/**
 * Exchanges a Google access token for your app's JWT.
 * @param accessToken Google access token from OAuth flow
 * @returns your backend JWT (string) on success
 */
export async function googleAuth(accessToken: string): Promise<string> {
  if (!accessToken) throw new Error("Missing Google access token");

  try {
    // Fetch user profile from Google
    // [2026-02-11] cookie-auth: Explicitly disable credentials for Google API (not our backend)
    const profileRes = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      withCredentials: false, // Google API doesn't accept credentials
    });

    const profile = profileRes.data;

    // Send to backend for authentication
    const { data } = await axios.post<string>(
      `${apiUrl}/api/users/auth/google`,
      {
        flow: "access_token",
        accessToken,
        profile: {
          sub: profile.sub,
          email: profile.email,
          email_verified: profile.email_verified,
          given_name: profile.given_name,
          family_name: profile.family_name,
          picture: profile.picture,
        },
        provider: "google",
        intent: "login", // For login flow
      },
      { withCredentials: true }, // [2026-02-11] Cookie auth: Enable cookie handling
    );
    return data;
  } catch (error: any) {
    console.error("Google auth error (raw):", error);

    // Axios-specific handling
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;
      const data: any = error.response.data || {};
      let message: string | undefined;

      // 1) If backend sends a top-level message
      if (typeof data.message === "string") {
        message = data.message;
      }

      // 2) If backend sends { errors: [{ message, path }] }
      if (!message && Array.isArray(data.errors) && data.errors.length > 0) {
        const first = data.errors[0];
        if (typeof first?.message === "string") {
          message = first.message;
        }
      }

      // Create enriched error with backend details
      const err = new Error(message || "Google authentication failed");
      (err as any).status = status;
      (err as any).code = data.code;
      (err as any).raw = data;

      throw err;
    }

    // Fallback for non-Axios or unexpected errors
    const genericErr = new Error("Google authentication failed");
    throw genericErr;
  }
}
